import * as THREE from "three";
import { runInAction } from "mobx";
import {
  SingleLineType,
  WallConnectionEnd,
  WallConnectionStart,
  WallType,
  isSingleLineType,
  isWallType,
} from "../../types/floorplannerTypes";
import {
  floorplannerStore,
  validateDragDelta,
  validateWallDimensions,
} from "../../store/floorplannerStore";
import { renderStore } from "../../store/renderStore";
import {
  cascadeCenterConnectionUpdates,
  updateSymbolAttachmentsDuringDrag,
} from "./updateWallConnections";
import { validateCenterConnectionConstraints } from "./dragMiddleVHandle";

/**
 * Rigidly translates a wall or single line by a world-space delta, cascading
 * connected endpoints the same way as a middle-handle drag (without snapping).
 * Used for keyboard nudge so room-bound floors stay aligned with wall geometry.
 */
export const translateWallOrLineByWorldDelta = (
  source: WallType | SingleLineType,
  worldDelta: THREE.Vector2,
): string[] => {
  const object = floorplannerStore.findObjectId(source.id);
  if (!object || (!isWallType(object) && !isSingleLineType(object))) {
    return [];
  }

  const deltaValidation = validateDragDelta(worldDelta);
  if (!deltaValidation.isValid) {
    return [];
  }

  const movedStart = object.start.clone().add(worldDelta);
  const movedEnd = object.end.clone().add(worldDelta);
  const constrainedPositions = validateCenterConnectionConstraints(
    object,
    movedStart,
    movedEnd,
  );
  let finalMovedStart = constrainedPositions ? constrainedPositions.start : movedStart;
  let finalMovedEnd = constrainedPositions ? constrainedPositions.end : movedEnd;

  const dimensionValidation = validateWallDimensions(finalMovedStart, finalMovedEnd);
  if (!dimensionValidation.isValid) {
    return [];
  }

  const actualDelta = new THREE.Vector2().subVectors(finalMovedStart, object.start);
  const affectedIds = new Set<string>([object.id]);

  const newControlPoint = object.controlPoint
    ? object.controlPoint.clone().add(actualDelta)
    : null;

  const updateConnectedObjectGeometry = (connectedObject: WallType | SingleLineType) => {
    try {
      if (isWallType(connectedObject)) {
        floorplannerStore.setObjectProperty(connectedObject.id, "cornerState", undefined);
        if (connectedObject.symbolAttachments?.length) {
          updateSymbolAttachmentsDuringDrag(connectedObject.id, floorplannerStore);
        }
        renderStore.composeWallShape(connectedObject.id, undefined, true);
        if (renderStore.isVisualUpdateAllowed(connectedObject.id)) {
          renderStore.generateOutlineSegments(connectedObject.id);
        }
      }
      renderStore.requestInvalidation(connectedObject.id);
    } catch (geometryError) {
      console.error("translateWallOrLineByWorldDelta: geometry update failed", {
        objectId: connectedObject.id,
        error: geometryError instanceof Error ? geometryError.message : geometryError,
      });
    }
  };

  const adjustConnectedObjectEndpoint = (
    connectedObject: WallType | SingleLineType,
    targetEndpoint: "start" | "end",
  ) => {
    if (targetEndpoint === "start") {
      const newStart = connectedObject.start.clone().add(actualDelta);
      floorplannerStore.updateObjectPosition(connectedObject.id, newStart, connectedObject.end, {
        bypassEdgeConstraints: true,
        skipInvalidation: true,
      });
    } else {
      const newEnd = connectedObject.end.clone().add(actualDelta);
      floorplannerStore.updateObjectPosition(
        connectedObject.id,
        connectedObject.start,
        newEnd,
        { bypassEdgeConstraints: true, skipInvalidation: true },
      );
    }
    affectedIds.add(connectedObject.id);
    if (isWallType(connectedObject)) {
      floorplannerStore.incrementWallGeometryVersion(connectedObject.id);
    }
    updateConnectedObjectGeometry(connectedObject);
  };

  const edgeConnections = isWallType(object) ? object.edgeConnections || [] : [];
  const centerConnections = object.centerConnections || [];
  const cascadeSourceIds = new Set<string>();

  const processEndpointConnections = (
    edgeSourceEndpoint: "start" | "end",
    centerSourcePosition: number,
  ) => {
    const processed = new Set<string>();

    edgeConnections
      .filter((conn) => conn.sourceEndpoint === edgeSourceEndpoint)
      .forEach((conn) => {
        const connectedObject = floorplannerStore.findObjectId(conn.targetWallId);
        if (!connectedObject || (!isWallType(connectedObject) && !isSingleLineType(connectedObject))) {
          return;
        }
        const targetEndpoint =
          conn.targetEndpoint === "start" || conn.targetEndpoint === "end"
            ? conn.targetEndpoint
            : null;
        const key = `${connectedObject.id}:${targetEndpoint ?? "center-edge"}`;
        if (processed.has(key)) return;
        processed.add(key);
        if (targetEndpoint) {
          adjustConnectedObjectEndpoint(connectedObject, targetEndpoint);
        } else {
          updateConnectedObjectGeometry(connectedObject);
        }
      });

    centerConnections
      .filter((conn) => conn.sourcePosition === centerSourcePosition)
      .forEach((conn) => {
        let targetEndpoint: "start" | "end" | null = null;
        if (conn.targetPosition === WallConnectionStart) {
          targetEndpoint = "start";
        } else if (conn.targetPosition === WallConnectionEnd) {
          targetEndpoint = "end";
        }
        const connectedObject = floorplannerStore.findObjectId(conn.id);
        if (!connectedObject || (!isWallType(connectedObject) && !isSingleLineType(connectedObject))) {
          return;
        }
        const key = `${connectedObject.id}:${targetEndpoint ?? "center"}`;
        if (processed.has(key)) return;
        processed.add(key);
        if (targetEndpoint) {
          adjustConnectedObjectEndpoint(connectedObject, targetEndpoint);
        } else {
          updateConnectedObjectGeometry(connectedObject);
        }
      });
  };

  processEndpointConnections("start", WallConnectionStart);
  processEndpointConnections("end", WallConnectionEnd);

  const middleConnections = centerConnections.filter(
    (conn) =>
      conn.sourcePosition !== WallConnectionStart &&
      conn.sourcePosition !== WallConnectionEnd,
  );
  const processedMiddleObjects = new Set<string>();

  middleConnections.forEach((conn) => {
    const connectedObject = floorplannerStore.findObjectId(conn.id);
    if (!connectedObject || (!isWallType(connectedObject) && !isSingleLineType(connectedObject))) {
      return;
    }
    const key = `${connectedObject.id}:${conn.targetPosition}`;
    if (processedMiddleObjects.has(key)) return;
    processedMiddleObjects.add(key);

    let actualConnectionPoint: THREE.Vector2;
    if (conn.targetPosition === WallConnectionStart) {
      actualConnectionPoint = connectedObject.start.clone();
    } else if (conn.targetPosition === WallConnectionEnd) {
      actualConnectionPoint = connectedObject.end.clone();
    } else {
      const connectedObjectMidpoint = new THREE.Vector2(
        (connectedObject.start.x + connectedObject.end.x) / 2,
        (connectedObject.start.y + connectedObject.end.y) / 2,
      );
      const objectDirection = new THREE.Vector2()
        .subVectors(object.end, object.start)
        .normalize();
      const toMidpoint = new THREE.Vector2().subVectors(connectedObjectMidpoint, object.start);
      const projection = toMidpoint.dot(objectDirection);
      actualConnectionPoint = new THREE.Vector2().addVectors(
        object.start,
        objectDirection.clone().multiplyScalar(projection),
      );
    }

    const newConnectionPoint = actualConnectionPoint.clone().add(actualDelta);

    if (conn.targetPosition === WallConnectionStart) {
      floorplannerStore.updateObjectPosition(
        connectedObject.id,
        newConnectionPoint.clone(),
        connectedObject.end,
        { bypassEdgeConstraints: true, skipInvalidation: true },
      );
    } else if (conn.targetPosition === WallConnectionEnd) {
      floorplannerStore.updateObjectPosition(
        connectedObject.id,
        connectedObject.start,
        newConnectionPoint.clone(),
        { bypassEdgeConstraints: true, skipInvalidation: true },
      );
    } else {
      floorplannerStore.updateObjectPosition(
        connectedObject.id,
        connectedObject.start.clone().add(actualDelta),
        connectedObject.end.clone().add(actualDelta),
        { bypassEdgeConstraints: true, skipInvalidation: true },
      );
    }

    if (isWallType(connectedObject)) {
      floorplannerStore.incrementWallGeometryVersion(connectedObject.id);
    }
    updateConnectedObjectGeometry(connectedObject);
    cascadeSourceIds.add(connectedObject.id);
    affectedIds.add(connectedObject.id);
  });

  cascadeCenterConnectionUpdates(
    Array.from(cascadeSourceIds),
    floorplannerStore,
    new Set([object.id]),
  ).forEach((cascadedId) => {
    affectedIds.add(cascadedId);
    const cascadedObject = floorplannerStore.findObjectId(cascadedId);
    if (!cascadedObject || (!isWallType(cascadedObject) && !isSingleLineType(cascadedObject))) {
      return;
    }
    if (isWallType(cascadedObject)) {
      floorplannerStore.incrementWallGeometryVersion(cascadedObject.id);
    }
    updateConnectedObjectGeometry(cascadedObject);
  });

  runInAction(() => {
    floorplannerStore.updateObjectPosition(object.id, finalMovedStart, finalMovedEnd, {
      bypassEdgeConstraints: true,
      skipInvalidation: true,
    });
    if (newControlPoint) {
      floorplannerStore.setObjectProperty(object.id, "controlPoint", newControlPoint);
    }
    if (isWallType(object)) {
      floorplannerStore.setObjectProperty(object.id, "cornerState", undefined);
      renderStore.composeWallShape(object.id, undefined, true);
      renderStore.trimShapes(object as WallType);
      if (renderStore.isVisualUpdateAllowed(object.id)) {
        renderStore.generateOutlineSegments(object.id);
      }
      const wall = object as WallType;
      if (wall.symbolAttachments?.length) {
        updateSymbolAttachmentsDuringDrag(wall.id, floorplannerStore);
      }
    }
    floorplannerStore.incrementWallGeometryVersion(object.id);
  });

  return Array.from(affectedIds);
};
